/** Format a sequential candidate number as a human-readable Candidate ID.
 *  If seekCandidateNumber is provided, the candidate originates from ForkSeek → SK-XXXXX.
 *  Otherwise, the candidate is ForkHire-only → FH-XXXXX.
 */
export function formatCandidateId(
  candidateNumber: number | null | undefined,
  seekCandidateNumber?: number | null,
): string {
  if (seekCandidateNumber) {
    return `SK-${String(seekCandidateNumber).padStart(5, '0')}`;
  }
  if (!candidateNumber) return '';
  return `FH-${String(candidateNumber).padStart(5, '0')}`;
}

/** Always returns the original ForkHire ID (FH-XXXXX) for audit/traceability,
 *  regardless of whether the candidate later registered on ForkSeek.
 *  Returns empty string if no candidate number exists.
 */
export function formatOriginalHireId(
  candidateNumber: number | null | undefined,
): string {
  if (!candidateNumber) return '';
  return `FH-${String(candidateNumber).padStart(5, '0')}`;
}

/** Returns a display-safe candidate name.
 *  Replaces internal placeholder names (cv-upload-pending, placeholder emails)
 *  with a user-friendly label so recruiters never see raw system identifiers.
 */
export function formatCandidateName(name: string | null | undefined): string {
  if (!name) return 'Unknown Candidate';
  if (
    name === 'cv-upload-pending' ||
    name.startsWith('cv-upload-') ||
    name.endsWith('@placeholder.local') ||
    name.trim().length < 2
  ) {
    return 'CV Processing…';
  }
  return name;
}

/** Returns both current display ID and the original FH-ID (if different).
 *  Useful for showing "SK-00001 (formerly FH-00042)" in UI or logs.
 */
export function formatCandidateIdWithHistory(
  candidateNumber: number | null | undefined,
  seekCandidateNumber?: number | null,
): { displayId: string; originalHireId: string; wasUpgraded: boolean } {
  const displayId = formatCandidateId(candidateNumber, seekCandidateNumber);
  const originalHireId = formatOriginalHireId(candidateNumber);
  const wasUpgraded = !!seekCandidateNumber && !!candidateNumber;
  return { displayId, originalHireId, wasUpgraded };
}
